import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../ui/dialog';
import { Plus, Trash2, Pencil, Award, Upload, ExternalLink } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TIERS = ['platinum', 'gold', 'silver', 'supporter'];

const blank = { name: '', tier: 'gold', logo_url: '', website: '', description: '', order: 0, active: true };

const SponsorsTab = () => {
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/admin/sponsors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSponsors(res.data.sponsors || []);
    } catch {
      toast.error('Failed to load sponsors');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm({ ...blank }); setIsOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...blank, ...s }); setIsOpen(true); };
  const closeDialog = () => { setIsOpen(false); setEditing(null); setForm({ ...blank }); };

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/api/admin/upload/image`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setForm(f => ({ ...f, logo_url: res.data.url }));
      toast.success('Logo uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
    setUploading(false);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Name required');
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      if (editing) {
        await axios.patch(`${API_URL}/api/admin/sponsors/${editing.id}`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Sponsor updated');
      } else {
        await axios.post(`${API_URL}/api/admin/sponsors`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Sponsor added');
      }
      setEditing(null);
      setForm({ ...blank });
      setIsOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this sponsor?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/admin/sponsors/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Deleted');
      load();
    } catch {
      toast.error('Delete failed');
    }
  };

  const toggleActive = async (s) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API_URL}/api/admin/sponsors/${s.id}`, { active: !s.active }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      load();
    } catch {
      toast.error('Update failed');
    }
  };

  return (
    <div className="space-y-4" data-testid="sponsors-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-display font-bold text-lg uppercase tracking-tight">Sponsors</h3>
          <p className="text-sm text-muted-foreground">Manage sponsor logos by tier. They appear on the public <a href="/sponsors" target="_blank" rel="noreferrer" className="text-primary underline">Sponsors page</a>.</p>
        </div>
        <Button onClick={openCreate} data-testid="add-sponsor-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Sponsor
        </Button>
      </div>

      <Dialog open={isOpen} onOpenChange={(open) => { if (!open) closeDialog(); }}>
        <DialogContent className="max-w-lg" data-testid="sponsor-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase">{editing ? 'Edit Sponsor' : 'Add Sponsor'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Seven Saddle" data-testid="sponsor-name" />
            </div>
            <div>
              <Label>Tier</Label>
              <Select value={form.tier} onValueChange={(v) => setForm(f => ({ ...f, tier: v }))}>
                <SelectTrigger data-testid="sponsor-tier"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIERS.map(t => <SelectItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Logo</Label>
              <div className="flex gap-2 items-center">
                <Input
                  placeholder="Paste logo URL or upload"
                  value={form.logo_url}
                  onChange={(e) => setForm(f => ({ ...f, logo_url: e.target.value }))}
                  data-testid="sponsor-logo-url"
                />
                <label className="cursor-pointer">
                  <Input type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} />
                  <Button type="button" variant="outline" disabled={uploading} asChild>
                    <span><Upload className="w-4 h-4 mr-1" /> {uploading ? 'Uploading...' : 'Upload'}</span>
                  </Button>
                </label>
              </div>
              {form.logo_url && (
                <div className="mt-2 p-3 border rounded bg-muted/30 flex justify-center">
                  <img src={form.logo_url} alt="Preview" className="max-h-24" />
                </div>
              )}
            </div>
            <div>
              <Label>Website</Label>
              <Input value={form.website} onChange={(e) => setForm(f => ({ ...f, website: e.target.value }))} placeholder="https://sevensaddle.com" />
            </div>
            <div>
              <Label>Short description (optional)</Label>
              <Textarea rows={2} value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Display Order</Label>
                <Input type="number" value={form.order} onChange={(e) => setForm(f => ({ ...f, order: parseInt(e.target.value) || 0 }))} />
              </div>
              <div className="flex flex-col">
                <Label>Active</Label>
                <Switch className="mt-3" checked={form.active} onCheckedChange={(v) => setForm(f => ({ ...f, active: v }))} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-sponsor-btn">{saving ? 'Saving...' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {loading ? (
        <div className="flex justify-center py-12"><div className="spinner" /></div>
      ) : sponsors.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Award className="w-12 h-12 mx-auto opacity-40 mb-2" />
          <p>No sponsors yet. Add your first one.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sponsors.map(s => (
            <Card key={s.id} className={!s.active ? 'opacity-60' : ''}>
              <CardContent className="p-4 space-y-2">
                <div className="aspect-video bg-muted/30 rounded flex items-center justify-center overflow-hidden">
                  {s.logo_url ? (
                    <img src={s.logo_url} alt={s.name} className="max-h-full max-w-full object-contain p-3" />
                  ) : (
                    <span className="text-muted-foreground text-sm">No logo</span>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <h4 className="font-display font-bold uppercase truncate">{s.name}</h4>
                  <Badge variant="secondary" className="capitalize">{s.tier}</Badge>
                </div>
                {s.website && (
                  <a href={s.website} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline flex items-center gap-1 truncate">
                    <ExternalLink className="w-3 h-3" /> {s.website}
                  </a>
                )}
                <div className="flex items-center justify-between pt-2 border-t border-border">
                  <div className="flex items-center gap-2 text-xs">
                    <Switch checked={s.active} onCheckedChange={() => toggleActive(s)} />
                    <span className="text-muted-foreground">{s.active ? 'Active' : 'Hidden'}</span>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(s)} data-testid={`edit-sponsor-${s.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(s.id)} data-testid={`delete-sponsor-${s.id}`}>
                      <Trash2 className="w-3.5 h-3.5 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default SponsorsTab;
